import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  addConversationParticipants,
  createConversation,
  deleteConversation,
  fetchConversation,
  fetchConversations,
  fetchMessages,
  kickParticipant,
  leaveConversation,
  pinChat,
  pinMessage,
  markConversationRead,
  renameConversation,
  sendConversationMessage,
  type ConversationCreatePayload,
  type ConversationListResponse,
  type ConversationRow,
  type MessageListResponse,
} from '../lib/api'
import { hasTokens } from '../lib/auth'

export function useConversations() {
  return useQuery<ConversationListResponse, ApiError>({
    queryKey: ['conversations'],
    queryFn: () => fetchConversations(),
    enabled: hasTokens(),
    staleTime: 10_000,
    // Poll più frequente (15s) + invalidazione live via SSE notifiche
    // (NotificationsBell su internal_message) per badge/lista quasi real-time.
    refetchInterval: 15_000,
    retry: false,
  })
}

export function useConversation(id: string | null) {
  return useQuery<ConversationRow, ApiError>({
    queryKey: ['conversation', id],
    queryFn: () => fetchConversation(id as string),
    enabled: hasTokens() && !!id,
    staleTime: 5_000,
    // Poll 10s: aggiorna last_read_at dei partecipanti → doppia spunta (✓✓)
    // si accende quando l'altro apre la chat (markRead è silenzioso, no SSE).
    refetchInterval: 10_000,
    retry: false,
  })
}

/** Messaggi di una conversazione, con ricerca testo opzionale (q). */
export function useConversationMessages(id: string | null, q?: string) {
  return useQuery<MessageListResponse, ApiError>({
    queryKey: ['messages', 'conversation', id, q ?? ''],
    queryFn: () =>
      fetchMessages({ conversation_id: id as string, q, page_size: 200 }),
    enabled: hasTokens() && !!id,
    staleTime: 5_000,
    // Chat aperta: poll 8s (fallback) + invalidazione live via SSE per i nuovi
    // messaggi in arrivo dagli altri senza dover ricaricare la pagina.
    refetchInterval: 8_000,
    retry: false,
  })
}

export function useSendConversationMessage(conversationId: string) {
  const qc = useQueryClient()
  return useMutation<
    unknown,
    ApiError,
    { body: string; attachment_ids?: string[] }
  >({
    mutationFn: ({ body, attachment_ids }) =>
      sendConversationMessage(conversationId, body, attachment_ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['messages', 'conversation', conversationId] })
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useCreateConversation() {
  const qc = useQueryClient()
  return useMutation<ConversationRow, ApiError, ConversationCreatePayload>({
    mutationFn: (payload) => createConversation(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useAddConversationParticipants(conversationId: string) {
  const qc = useQueryClient()
  return useMutation<ConversationRow, ApiError, string[]>({
    mutationFn: (userIds) => addConversationParticipants(conversationId, userIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] })
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useMarkConversationRead() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => markConversationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useRenameConversation(conversationId: string) {
  const qc = useQueryClient()
  return useMutation<ConversationRow, ApiError, string>({
    mutationFn: (title) => renameConversation(conversationId, title),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] })
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useDeleteConversation() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteConversation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useLeaveConversation() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, { id: string; newOwnerId?: string }>({
    mutationFn: ({ id, newOwnerId }) => leaveConversation(id, newOwnerId),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['conversation', id] })
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function useKickParticipant(conversationId: string) {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (userId) => kickParticipant(conversationId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] })
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

export function usePinMessage(conversationId: string) {
  const qc = useQueryClient()
  return useMutation<ConversationRow, ApiError, string | null>({
    mutationFn: (messageId) => pinMessage(conversationId, messageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] })
    },
  })
}

export function usePinChat() {
  const qc = useQueryClient()
  return useMutation<
    void,
    ApiError,
    { targetKind: 'conversation' | 'client'; targetId: string; pinned: boolean }
  >({
    mutationFn: ({ targetKind, targetId, pinned }) => pinChat(targetKind, targetId, pinned),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations'] })
      void qc.invalidateQueries({ queryKey: ['messages', 'threads'] })
    },
  })
}
